// src/pages/personal/analytics/FeedbackSection/hooks/useFeedbackData.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { Period, DateRange } from '@/components/ui/PeriodSelector';

// Типы для параметров и возвращаемых данных
interface UseFeedbackDataParams {
  period: Period;
  platforms?: string[];
  ratingRange?: { min?: number; max?: number };
  dateRange?: DateRange;
}

interface UseFeedbackDataReturn {
  data: any;  // Тип для данных можно уточнить в соответствии с API
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

export const useFeedbackData = (params: UseFeedbackDataParams): UseFeedbackDataReturn => {
  const { t } = useTranslation(['analytics', 'errors']);
  const { businessId, isLoading: isBusinessIdLoading } = useBusinessId();
  
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Флаг для контроля активности компонента
  const isMounted = useRef(true);
  // Флаг для отслеживания первого рендера
  const isFirstRender = useRef(true);
  // Предыдущие параметры для сравнения
  const prevParams = useRef<any>({});
  // Счетчик запросов для отладки
  const fetchCounter = useRef(0);
  // Флаг для предотвращения множественных одновременных запросов
  const isRequestInProgress = useRef(false);
  
  // Функция сравнения объектов для определения изменений
  const areParamsEqual = useCallback((prev: any, curr: any) => {
    // Проверка базовых полей
    if (prev.period !== curr.period) {
      console.log('Изменился period:', prev.period, ' -> ', curr.period);
      return false;
    }
    
    // Проверка платформ
    if (JSON.stringify(prev.platforms) !== JSON.stringify(curr.platforms)) {
      console.log('Изменились platforms');
      return false;
    }
    
    // Проверка диапазона рейтингов
    if (JSON.stringify(prev.ratingRange) !== JSON.stringify(curr.ratingRange)) {
      console.log('Изменился диапазон рейтингов');
      return false;
    }
    
    // Проверка бизнес ID
    if (prev.businessId !== curr.businessId) {
      console.log('Изменился businessId');
      return false;
    }
    
    // Сравнение диапазонов дат
    if (prev.dateRange && curr.dateRange) {
      // Проверка валидности дат
      const prevStartValid = prev.dateRange.startDate instanceof Date && 
                            !isNaN(prev.dateRange.startDate.getTime());
      const prevEndValid = prev.dateRange.endDate instanceof Date && 
                          !isNaN(prev.dateRange.endDate.getTime());
      const currStartValid = curr.dateRange.startDate instanceof Date && 
                            !isNaN(curr.dateRange.startDate.getTime());
      const currEndValid = curr.dateRange.endDate instanceof Date && 
                          !isNaN(curr.dateRange.endDate.getTime());
      
      // Если какая-то из дат недействительна, считаем, что параметры изменились
      if (!prevStartValid || !prevEndValid || !currStartValid || !currEndValid) {
        console.log('Одна из дат недействительна');
        return false;
      }
      
      // Сравниваем даты
      if (prev.dateRange.startDate.getTime() !== curr.dateRange.startDate.getTime()) {
        console.log('Изменилась начальная дата:', 
          prev.dateRange.startDate.toISOString(), ' -> ', 
          curr.dateRange.startDate.toISOString());
        return false;
      }
      
      if (prev.dateRange.endDate.getTime() !== curr.dateRange.endDate.getTime()) {
        console.log('Изменилась конечная дата:', 
          prev.dateRange.endDate.toISOString(), ' -> ', 
          curr.dateRange.endDate.toISOString());
        return false;
      }
    } else if ((prev.dateRange && !curr.dateRange) || (!prev.dateRange && curr.dateRange)) {
      console.log('Изменилось наличие dateRange');
      return false;
    }
    
    console.log('Все параметры одинаковые');
    return true;
  }, []);
  
  // Основная функция для получения данных
  const fetchFeedbackData = useCallback(async () => {
    // Проверяем, не выполняется ли уже запрос
    if (isRequestInProgress.current) {
      console.log('Запрос уже выполняется, пропускаем дублирующий запрос');
      return;
    }
    
    // Устанавливаем флаг выполнения запроса
    isRequestInProgress.current = true;
    
    // Увеличиваем счетчик запросов для отладки
    fetchCounter.current += 1;
    const requestId = fetchCounter.current;
    
    console.log(`[${requestId}] Запрос данных отзывов начат`);
    
    if (isBusinessIdLoading || !businessId || !isMounted.current) {
      console.log(`[${requestId}] Запрос отменен: businessId загружается или компонент размонтирован`);
      isRequestInProgress.current = false;
      return;
    }
    
    // Проверяем валидность dateRange
    const isDateRangeValid = params.dateRange && 
      params.dateRange.startDate instanceof Date && !isNaN(params.dateRange.startDate.getTime()) &&
      params.dateRange.endDate instanceof Date && !isNaN(params.dateRange.endDate.getTime());
    
    // Формируем текущие параметры
    const currentParams = {
      period: params.period,
      platforms: params.platforms,
      ratingRange: params.ratingRange,
      dateRange: isDateRangeValid ? params.dateRange : undefined,
      businessId
    };
    
    // Сохраняем последнюю конфигурацию
    console.log(`[${requestId}] Текущие параметры:`, {
      period: params.period,
      platforms: params.platforms?.length,
      dateRange: isDateRangeValid ? {
        startDate: params.dateRange?.startDate.toISOString(), 
        endDate: params.dateRange?.endDate.toISOString()  
      } : undefined,
      isFirstRender: isFirstRender.current
    });
    
    // Если не первый рендер, проверяем изменение параметров
    if (!isFirstRender.current) {
      console.log(`[${requestId}] Проверка изменения параметров`);
      
      // Если параметры не изменились, пропускаем запрос
      if (areParamsEqual(prevParams.current, currentParams)) {
        console.log(`[${requestId}] Параметры не изменились, пропускаем запрос`);
        isRequestInProgress.current = false;
        return;
      }
    }
    
    // Сбрасываем флаг первого рендера
    isFirstRender.current = false;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Формируем параметры запроса
      const apiParams: any = {
        business: businessId.toString(),
        period: params.period
      };
      
      // Добавляем параметры платформ
      if (params.platforms && params.platforms.length > 0) {
        apiParams.platform = params.platforms.join(',');
      }
      
      // Добавляем параметры диапазона рейтингов
      if (params.ratingRange) {
        if (params.ratingRange.min !== undefined) {
          apiParams.rating_min = params.ratingRange.min;
        }
        if (params.ratingRange.max !== undefined) {
          apiParams.rating_max = params.ratingRange.max;
        }
      }
      
      // Для кастомного периода добавляем start_date и end_date
      if (params.period === 'custom' && isDateRangeValid && params.dateRange) {
        apiParams.start_date = params.dateRange.startDate.toISOString();
        apiParams.end_date = params.dateRange.endDate.toISOString();
      }
      
      console.log(`[${requestId}] Запрос к API с параметрами:`, apiParams);
      
      // Выполняем запрос к API
      const response = await api.get('/api/analytics/feedback/', apiParams);
      
      // Проверяем активность компонента
      if (!isMounted.current) {
        console.log(`[${requestId}] Компонент размонтирован, отменяем обработку ответа`);
        isRequestInProgress.current = false;
        return;
      }
      
      console.log(`[${requestId}] Получен ответ от API`, 
        response ? 'с данными' : 'без данных');
      
      // Обновляем prevParams только после успешного запроса
      prevParams.current = currentParams;
      
      // Обрабатываем полученные данные
      if (response) {
        setData(response);
      } else {
        setError(t('errors:feedback_loading_error'));
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error(`[${requestId}] Ошибка загрузки данных:`, error);
        setError(error.message || t('errors:feedback_loading_error'));
      }
    } finally {
      if (isMounted.current) {
        console.log(`[${requestId}] Завершение запроса`);
        setIsLoading(false);
      }
      // Снимаем флаг выполнения запроса
      isRequestInProgress.current = false;
    }
  }, [businessId, isBusinessIdLoading, params, t, areParamsEqual]);
  
  // Эффект для инициализации и обновления данных
  useEffect(() => {
    console.log('Монтирование компонента useFeedbackData, настройка параметров');
    isMounted.current = true;
    
    // Запускаем первичную загрузку данных
    fetchFeedbackData();
    
    return () => {
      console.log('Размонтирование компонента useFeedbackData');
      isMounted.current = false;
    };
  }, [fetchFeedbackData]);
  
  // Публичный метод для принудительного обновления данных
  const refreshData = useCallback(async () => {
    console.log('Принудительное обновление данных');
    await fetchFeedbackData();
  }, [fetchFeedbackData]);
  
  return {
    data,
    isLoading,
    error,
    refreshData
  };
};

export default useFeedbackData;